/**
 * FrameDecoder - Visual frame decoder for Protocol B
 *
 * Provides detailed, educational breakdown of protocol frames
 * with bit-level analysis and explanations.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  Binary,
  Hash,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  Cpu,
  ArrowRight,
  Radio,
  Zap,
  BookOpen,
  Copy,
  Check
} from 'lucide-react';
import {
  ParsedFrame,
  ParsedByte,
  ParsedBit,
  ParsedHeader,
  ParsedPhysicalId,
  ParsedCommand,
  CHANNEL_INFO
} from '../../utils/frameParser';
import { FrameType } from '../../types/protocol';

interface FrameDecoderProps {
  parsedFrame: ParsedFrame | null;
}

// Collapsible section component
function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  badge,
  badgeColor = 'bg-gray-600'
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: string;
  badgeColor?: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-750 text-left"
      >
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
        <Icon className="w-4 h-4 text-blue-400" />
        <span className="text-sm font-medium text-gray-200">{title}</span>
        {badge && (
          <span className={`ml-auto text-xs px-2 py-0.5 rounded ${badgeColor} text-white`}>
            {badge}
          </span>
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-gray-900/50">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Byte display with hover details
function ByteDisplay({ byte, showBits = false }: { byte: ParsedByte; showBits?: boolean }) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="relative">
      <div
        className="flex items-center gap-2 p-2 bg-gray-800 rounded cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={() => setShowDetails(!showDetails)}
      >
        <div className="w-8 text-xs text-gray-500 font-mono">[{byte.offset}]</div>
        <div className="font-mono text-green-400 font-medium">{byte.hex}</div>
        <div className="flex-1 text-xs text-gray-400">{byte.name}</div>
        {byte.bits && (
          <Binary className="w-3 h-3 text-gray-500" />
        )}
      </div>

      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="mt-1 p-2 bg-gray-800/80 rounded border border-gray-700 text-xs"
          >
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <span className="text-gray-500">Hex: </span>
                <span className="text-green-400 font-mono">{byte.hex}</span>
              </div>
              <div>
                <span className="text-gray-500">Dec: </span>
                <span className="text-blue-400 font-mono">{byte.value}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">Bin: </span>
                <span className="text-purple-400 font-mono">{byte.binary}</span>
              </div>
            </div>
            <div className="text-gray-300">{byte.description}</div>

            {/* Bit breakdown */}
            {showBits && byte.bits && (
              <div className="mt-3 pt-2 border-t border-gray-700">
                <div className="text-gray-500 mb-2">Desglose de bits:</div>
                <div className="space-y-1">
                  {byte.bits.map((bit, i) => (
                    <BitDisplay key={i} bit={bit} />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Individual bit display
function BitDisplay({ bit }: { bit: ParsedBit }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className={`w-5 h-5 rounded flex items-center justify-center font-mono font-bold ${
        bit.value ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'
      }`}>
        {bit.value ? '1' : '0'}
      </div>
      <div className="text-gray-500 w-8">b{bit.position}</div>
      <div className="text-gray-400 w-24">{bit.name}</div>
      <div className="text-gray-300 flex-1">{bit.description}</div>
    </div>
  );
}

// Physical ID visualization
function PhysicalIdDisplay({ id, label }: { id: ParsedPhysicalId; label: string }) {
  return (
    <div className="p-3 bg-gray-800 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <Radio className="w-4 h-4 text-blue-400" />
        <span className="text-sm font-medium text-gray-200">{label}</span>
      </div>

      <div className="font-mono text-lg text-green-400 mb-2">{id.formatted}</div>

      <div className="grid grid-cols-4 gap-1 text-xs">
        <div className="text-center p-1 bg-purple-900/50 rounded">
          <div className="text-purple-300">HOST</div>
          <div className="text-white font-mono">{id.host}</div>
        </div>
        <div className="text-center p-1 bg-blue-900/50 rounded">
          <div className="text-blue-300">CENTRAL</div>
          <div className="text-white font-mono">{id.central}</div>
        </div>
        <div className="text-center p-1 bg-green-900/50 rounded">
          <div className="text-green-300">REG</div>
          <div className="text-white font-mono">{id.regulator}</div>
        </div>
        <div className="text-center p-1 bg-cyan-900/50 rounded">
          <div className="text-cyan-300">SUB</div>
          <div className="text-white font-mono">{id.subregulator}</div>
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-400">{id.explanation}</div>
    </div>
  );
}

// Command display
function CommandDisplay({ command }: { command: ParsedCommand }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Zap className="w-5 h-5 text-yellow-400" />
        <div>
          <div className="font-mono text-yellow-400 font-medium">{command.name}</div>
          <div className="text-xs text-gray-500">{command.category}</div>
        </div>
        <div className="ml-auto font-mono text-xs text-gray-500">
          0x{command.code.toString(16).toUpperCase()}
        </div>
      </div>

      <div className="text-sm text-gray-300 bg-gray-800 rounded p-2">
        {command.description}
      </div>

      {command.parameters.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Parámetros</div>
          {command.parameters.map((param, i) => (
            <div key={i} className="p-2 bg-gray-800 rounded">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-blue-400">{param.name}</span>
                <span className="font-mono text-xs text-gray-500">
                  [{param.offset}:{param.length}B]
                </span>
              </div>
              <div className="font-mono text-green-400">
                {typeof param.decodedValue === 'string' ? param.decodedValue : param.decodedValue}
                {param.unit && <span className="text-gray-500 ml-1">{param.unit}</span>}
              </div>
              <div className="text-xs text-gray-400 mt-1">{param.description}</div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500">
        <span className="text-gray-400">Respuesta esperada: </span>
        {command.expectedResponse}
      </div>
    </div>
  );
}

// Educational notes display
function EducationalNotes({ notes }: { notes: string[] }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(notes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <button
        onClick={copyToClipboard}
        className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-300"
        title="Copiar"
      >
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-3 text-sm">
        {notes.map((note, i) => (
          <div
            key={i}
            className={`${note === '' ? 'h-2' : ''} ${
              note.startsWith('•') ? 'pl-2 text-gray-300' :
              note.startsWith('📡') || note.startsWith('🔐') || note.startsWith('📍') ||
              note.startsWith('🔄') || note.startsWith('🔗') || note.startsWith('🔍') ||
              note.startsWith('📋') || note.startsWith('📊') || note.startsWith('📨') ||
              note.startsWith('❓') || note.startsWith('⚠️')
                ? 'text-blue-300 font-medium'
                : 'text-gray-400'
            }`}
          >
            {note}
          </div>
        ))}
      </div>
    </div>
  );
}

// Raw hex display with copy
function RawHexDisplay({ hex, label }: { hex: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(hex.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2 p-2 bg-gray-800 rounded">
      <span className="text-xs text-gray-500">{label}:</span>
      <code className="flex-1 font-mono text-xs text-green-400 break-all">{hex}</code>
      <button
        onClick={copyToClipboard}
        className="p-1 text-gray-500 hover:text-gray-300"
        title="Copiar"
      >
        {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
}

// Header visualization
function HeaderDisplay({ header }: { header: ParsedHeader }) {
  return (
    <div className="space-y-4">
      {/* Visual flow */}
      <div className="flex items-center justify-center gap-2 p-3 bg-gray-800 rounded-lg">
        <div className="text-center">
          <div className="text-xs text-gray-500">Origen</div>
          <div className="font-mono text-sm text-blue-400">{header.source.formatted}</div>
          <div className="text-xs text-gray-600">
            Canal {header.sourceChannel}
          </div>
        </div>
        <ArrowRight className={`w-6 h-6 ${header.isResponse ? 'text-green-400' : 'text-yellow-400'}`} />
        <div className="text-center">
          <div className="text-xs text-gray-500">Destino</div>
          <div className="font-mono text-sm text-green-400">{header.destination.formatted}</div>
          <div className="text-xs text-gray-600">
            Canal {header.destinationChannel}
          </div>
        </div>
      </div>

      {/* Flags */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className={`p-2 rounded text-center ${header.isResponse ? 'bg-green-900/50 text-green-300' : 'bg-yellow-900/50 text-yellow-300'}`}>
          {header.isResponse ? 'RESPUESTA' : 'ENVÍO'}
        </div>
        <div className={`p-2 rounded text-center ${header.isMaintenance ? 'bg-purple-900/50 text-purple-300' : 'bg-blue-900/50 text-blue-300'}`}>
          {header.isMaintenance ? 'MANTENIMIENTO' : 'INFORMACIÓN'}
        </div>
        <div className={`p-2 rounded text-center ${header.expectsResponse ? 'bg-cyan-900/50 text-cyan-300' : 'bg-gray-700 text-gray-400'}`}>
          {header.expectsResponse ? 'ESPERA RESP.' : 'SIN RESP.'}
        </div>
      </div>

      {/* Error/continuation flags if applicable */}
      {(header.hasError || header.isContinuation) && (
        <div className="flex gap-2 text-xs">
          {header.hasError && (
            <div className="p-2 rounded bg-red-900/50 text-red-300 flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              {header.isCommunicationError ? 'ERROR COMUNICACIÓN' : 'ERROR'}
            </div>
          )}
          {header.isContinuation && (
            <div className="p-2 rounded bg-orange-900/50 text-orange-300">
              MENSAJE PARTIDO
            </div>
          )}
        </div>
      )}

      {/* Counter */}
      <div className="flex items-center gap-2 text-sm">
        <Hash className="w-4 h-4 text-gray-500" />
        <span className="text-gray-400">Contador:</span>
        <span className="font-mono text-white">{header.counter}</span>
        <span className="text-xs text-gray-500">(para correlacionar respuestas)</span>
      </div>

      {/* Physical IDs */}
      <div className="grid grid-cols-2 gap-3">
        <PhysicalIdDisplay id={header.source} label="Origen" />
        <PhysicalIdDisplay id={header.destination} label="Destino" />
      </div>

      {/* Byte-level breakdown */}
      <div className="space-y-1">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
          Desglose de bytes de cabecera
        </div>
        {header.bytes.map((byte, i) => (
          <ByteDisplay key={i} byte={byte} showBits={i < 2} />
        ))}
      </div>
    </div>
  );
}

// Main decoder component
export function FrameDecoder({ parsedFrame }: FrameDecoderProps) {
  if (!parsedFrame) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500 p-8">
        <Cpu className="w-16 h-16 mb-4 opacity-30" />
        <div className="text-lg font-medium mb-2">Decodificador de Tramas</div>
        <div className="text-sm text-center max-w-xs">
          Selecciona una trama en el monitor para ver su análisis detallado según el Protocolo B
        </div>
      </div>
    );
  }

  const frameTypeColors: Record<FrameType, string> = {
    [FrameType.TEST_COMMUNICATION]: 'bg-cyan-600',
    [FrameType.IDENTIFICATION]: 'bg-purple-600',
    [FrameType.TEST_RESPONSE]: 'bg-blue-600',
    [FrameType.MAINTENANCE]: 'bg-yellow-600',
    [FrameType.INFORMATION]: 'bg-green-600',
    [FrameType.FLOW_CONTROL]: 'bg-gray-600',
    [FrameType.ERROR]: 'bg-red-600'
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
        <div className={`px-3 py-1 rounded text-sm font-medium text-white ${frameTypeColors[parsedFrame.frameType]}`}>
          {parsedFrame.frameTypeName}
        </div>
        {parsedFrame.crc && (
          <div className={`flex items-center gap-1 text-sm ${parsedFrame.crc.valid ? 'text-green-400' : 'text-red-400'}`}>
            {parsedFrame.crc.valid ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            CRC {parsedFrame.crc.valid ? 'OK' : 'ERROR'}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="p-3 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg border-l-4 border-blue-500">
        <div className="text-sm text-gray-300">{parsedFrame.summary}</div>
        <div className="text-xs text-gray-500 mt-1">{parsedFrame.frameTypeDescription}</div>
      </div>

      {/* Raw data */}
      <Section title="Datos en bruto" icon={Binary} defaultOpen={false}>
        <div className="space-y-2">
          <RawHexDisplay hex={parsedFrame.rawHex} label="RAW" />
          {parsedFrame.decodedData && (
            <RawHexDisplay
              hex={Array.from(parsedFrame.decodedData).map(b => b.toString(16).toUpperCase().padStart(2, '0')).join(' ')}
              label="Decodificado (sin DLE)"
            />
          )}
        </div>
      </Section>

      {/* Test frame details */}
      {parsedFrame.testName && (
        <Section title="Test de Comunicaciones" icon={Radio} badge={parsedFrame.testName?.split(' ')[0]}>
          <div className="space-y-3">
            <div className="text-lg font-mono text-cyan-400">{parsedFrame.testName}</div>
            <div className="text-sm text-gray-300">{parsedFrame.testDescription}</div>
            <div className="p-2 bg-gray-800 rounded">
              <span className="text-gray-500">Código: </span>
              <span className="font-mono text-green-400">
                0x{parsedFrame.testCode?.toString(16).toUpperCase().padStart(2, '0')}
              </span>
            </div>
          </div>
        </Section>
      )}

      {/* Flow control details */}
      {parsedFrame.flowControlType && (
        <Section title="Control de Flujo" icon={FileText}>
          <div className="space-y-3">
            <div className="text-lg font-mono text-gray-300">{parsedFrame.flowControlType}</div>
            <div className="text-sm text-gray-400">{parsedFrame.flowControlDescription}</div>
          </div>
        </Section>
      )}

      {/* Header (for routed frames) */}
      {parsedFrame.header && (
        <Section
          title="Cabecera de Encaminamiento"
          icon={ArrowRight}
          badge="7 bytes"
          badgeColor="bg-blue-600"
        >
          <HeaderDisplay header={parsedFrame.header} />
        </Section>
      )}

      {/* Command */}
      {parsedFrame.command && (
        <Section
          title="Orden / Comando"
          icon={Zap}
          badge={parsedFrame.command.name}
          badgeColor="bg-yellow-600"
        >
          <CommandDisplay command={parsedFrame.command} />
        </Section>
      )}

      {/* CRC */}
      {parsedFrame.crc && (
        <Section
          title="CRC-16"
          icon={Hash}
          badge={parsedFrame.crc.valid ? 'VÁLIDO' : 'ERROR'}
          badgeColor={parsedFrame.crc.valid ? 'bg-green-600' : 'bg-red-600'}
          defaultOpen={false}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-2 bg-gray-800 rounded">
                <div className="text-xs text-gray-500">CRC Recibido</div>
                <div className="font-mono text-lg text-green-400">
                  0x{parsedFrame.crc.received.toString(16).toUpperCase().padStart(4, '0')}
                </div>
              </div>
              <div className={`p-2 rounded ${parsedFrame.crc.valid ? 'bg-green-900/30' : 'bg-red-900/30'}`}>
                <div className="text-xs text-gray-500">Estado</div>
                <div className={`text-lg ${parsedFrame.crc.valid ? 'text-green-400' : 'text-red-400'}`}>
                  {parsedFrame.crc.valid ? '✓ Válido' : '✗ Inválido'}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-400">
              Polinomio: x¹⁶ + x¹⁵ + x² + 1 (CRC-16-IBM)
            </div>
          </div>
        </Section>
      )}

      {/* Educational notes */}
      {parsedFrame.educationalNotes.length > 0 && (
        <Section title="Notas Educativas" icon={BookOpen} defaultOpen={true}>
          <EducationalNotes notes={parsedFrame.educationalNotes} />
        </Section>
      )}
    </div>
  );
}
